import { useEffect, useRef, useState } from "react";
import { useLazyGetWorkoutQuery } from "../libs/workout/api";
import { Workout } from "../libs/workout/types";
import {
  captureElementsToPdf,
  sanitizeFileName,
} from "../libs/workout/pdfImages";
import { WorkoutTables } from "./WorkoutTables";

type Props = {
  workoutId: string;
  workoutName?: string;
  /** Nome utente per l'header PDF ("Cliente: ..."). Omesso per template. */
  userName?: string;
  /** User ID passato a WorkoutTables per lookup esercizi. */
  userId?: number;
  /** Timestamp creazione per filename e header. */
  createdAt?: number | null;
  className?: string;
};

export const DownloadPdfButton: React.FC<Props> = ({
  workoutId,
  workoutName,
  userName,
  userId,
  createdAt,
  className,
}) => {
  const [triggerGetWorkout] = useLazyGetWorkoutQuery();
  const [isDownloading, setIsDownloading] = useState(false);
  const [captureState, setCaptureState] = useState<{
    workout: Workout;
    fileName: string;
  } | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const workout = await triggerGetWorkout(workoutId).unwrap();
      const name = workoutName || workout.name || "scheda";
      const dateSuffix = createdAt
        ? "_" + new Date(createdAt).toISOString().slice(0, 10)
        : "";
      const fileName = sanitizeFileName(name) + dateSuffix + ".pdf";
      setCaptureState({ workout, fileName });
    } catch (err) {
      console.error(err);
      alert("Errore durante la generazione del PDF");
      setIsDownloading(false);
    }
  };

  // Quando il render nascosto e' montato, cattura i giorni e genera il PDF.
  useEffect(() => {
    if (!captureState) return;
    let cancelled = false;

    const run = async () => {
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => setTimeout(r, 450));
      try {
        await (document as unknown as { fonts?: { ready: Promise<unknown> } })
          .fonts?.ready;
      } catch {}

      const container = captureRef.current;
      if (cancelled || !container) return;

      const headerNode = container.querySelector<HTMLElement>(
        "[data-workout-header]"
      );
      const dayNodes = Array.from(
        container.querySelectorAll<HTMLElement>("[data-workout-day]")
      );
      const allNodes = [
        ...(headerNode ? [headerNode] : []),
        ...dayNodes,
      ];

      try {
        if (dayNodes.length === 0) {
          alert("Questa scheda non ha giorni da esportare.");
        } else {
          await captureElementsToPdf(allNodes, captureState.fileName);
        }
      } catch (err) {
        console.error(err);
        alert("Errore durante la generazione del PDF");
      } finally {
        if (!cancelled) {
          setCaptureState(null);
          setIsDownloading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [captureState]);

  const captureWeeks = captureState?.workout.data?.length || 1;
  const captureWidth = Math.min(
    1800,
    Math.max(1100, 380 + captureWeeks * 220)
  );

  const headerName = userName || captureState?.workout.name || workoutName || "Scheda";

  return (
    <>
      <button
        type="button"
        onClick={handleDownload}
        disabled={isDownloading}
        title="Scarica PDF"
        className={`inline-flex items-center justify-center p-1 rounded text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-600 disabled:opacity-50 transition-colors ${className || ""}`}
      >
        {isDownloading ? (
          <svg
            className="animate-spin w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12z"
            />
          </svg>
        ) : (
          <svg
            aria-hidden="true"
            className="w-4 h-4 fill-current"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm6.293-2.293a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L11 11.586V3a1 1 0 10-2 0v8.586L6.707 9.293a1 1 0 00-1.414 1.414l4 4z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>

      {/* Render nascosto per cattura screenshot → PDF */}
      {captureState && (
        <div
          aria-hidden
          ref={captureRef}
          style={{
            position: "fixed",
            left: 0,
            top: 0,
            zIndex: -1,
            width: captureWidth,
            background: "#111827",
            padding: 16,
            pointerEvents: "none",
          }}
        >
          <div
            data-workout-header
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "24px 24px 16px 24px",
              marginBottom: 16,
              background: "#111827",
            }}
          >
            <img
              src="/logo-fitshape.jpg"
              alt="FitShape.Lab"
              style={{
                width: 80,
                height: 80,
                objectFit: "contain",
                borderRadius: 8,
              }}
              crossOrigin="anonymous"
            />
            <div style={{ fontSize: 20, fontWeight: 700, color: "#ffffff" }}>
              {headerName}
            </div>
          </div>
          <WorkoutTables
            workout={captureState.workout}
            userId={userId}
          />
        </div>
      )}
    </>
  );
};
